import { STORES } from "../model/stores.js";
/**
 * What to call the seller on screen.
 *
 * Our own label for a shop we know, Google's string for everyone else. The raw
 * `source` is not consistent even within one response — a single lookup came
 * back with both `Amazon.in` and `amazon.in` — so showing it verbatim puts two
 * spellings of one shop in one row, and "Buy on Amazon.in" under a button
 * carrying the Amazon logo.
 */
export function sellerName(offer) {
    const store = offer.store ? STORES.find((s) => s.id === offer.store) : null;
    return store?.label ?? offer.seller;
}
/** How many are worth showing. Beyond this the row is a catalogue. */
export const MAX_CARDS = 8;
/**
 * Where a card takes you.
 *
 * A shop we know gets OUR deep link, so it opens the shop's own app rather
 * than a web page (ADR 0023) — and it searches for the card's own title rather
 * than the household's word for it, because the user has just pointed at that
 * packet. Verified live: searching Zepto and Blinkit for a full listing title
 * returns the product, and Blinkit even normalises the punctuation away.
 *
 * Everyone else gets Google's listing page, which is the only address we have
 * for them. Falls back to the household's own query when a listing has no
 * title, so a card always goes somewhere.
 */
export function cardHref(offer, query) {
    const store = offer.store ? STORES.find((s) => s.id === offer.store) : null;
    if (store)
        return store.href(offer.title || query);
    return offer.link ?? "";
}
/**
 * Shops we can open first, cheapest within each group, one card per packet.
 *
 * The grouping comes before the price, and that is a deliberate choice about
 * what the row is FOR. A card from a shop we know opens that shop's own app
 * with the product searched; a card from anyone else opens a Google page in a
 * browser. The first is worth more than a couple of rupees, so the shops the
 * household actually has get the front of the row — and inside each group the
 * cheapest leads, which is the comparison the row exists to make.
 *
 * The badge does NOT follow that order. It marks the genuinely lowest price
 * anywhere in the row, wherever it landed, because a "Cheapest" tag that meant
 * "cheapest of the ones we like" would be a lie told by omission.
 *
 * Deduped on seller AND title because the same shop lists the same product more
 * than once — different sizes read as different titles and stay, which is
 * right, while a genuine repeat collapses to its cheaper row.
 */
export function offerCards(offers, query) {
    const cheapestOf = new Map();
    for (const o of offers) {
        const key = `${o.seller.toLowerCase()}|${o.title.toLowerCase()}`;
        const held = cheapestOf.get(key);
        if (!held || o.priceMinor < held.priceMinor)
            cheapestOf.set(key, o);
    }
    const ranked = [...cheapestOf.values()]
        .map((o) => ({ ...o, href: cardHref(o, query), best: false }))
        // A card with nowhere to go is a card that cannot be chosen.
        .filter((c) => c.href)
        .sort((a, b) => {
        const known = Number(!!b.store) - Number(!!a.store);
        return known || a.priceMinor - b.priceMinor;
    })
        .slice(0, MAX_CARDS);
    // Marked after the cap, so the badge always names a card that is on screen.
    if (ranked.length > 1) {
        let low = ranked[0];
        for (const c of ranked)
            if (c.priceMinor < low.priceMinor)
                low = c;
        low.best = true;
    }
    return ranked;
}
//# sourceMappingURL=cards.js.map